import { deltaE, parseColor, parseTemplate, serializeTemplate, srgbToOklab, type MockupTemplate, type MockupView, type Rgb } from '@eip/engine';

/**
 * One blank the app can render into, wherever it came from. The photo is NOT held
 * here as pixels: a built-in carries the URL of its static asset and a user
 * template the Blob IndexedDB stored, and the runner's `fetchPhoto` turns either
 * into the bytes the worker prepares (once — the prepared maps then live there).
 */
export interface TemplateSource {
  kind: 'builtin' | 'user';
  template: MockupTemplate;
  /** Built-ins only: `${base}/${style}/${photo.src}`. */
  photoUrl?: string;
  /** User templates only: the photo exactly as it was imported. */
  photoBlob?: Blob;
  /** The shop-facing name of the style, from the style index that shipped it
   *  (`groupByStyle` falls back to the slug for a template with no index). */
  styleName?: string;
}

/** Where the user's templates are actually being kept. `'memory'` means this
 *  session only — the one line the editor and the panel show when it happens. */
export type TemplateStoreKind = 'indexeddb' | 'memory';

/** Said in both places a template can be authored (the editor and "My
 *  templates"), so the two cannot drift. */
export const SESSION_ONLY_NOTICE = 'Templates are kept for this session only (browser storage unavailable)';

export interface TemplateStore {
  list(): Promise<TemplateSource[]>;
  put(template: MockupTemplate, photo: Blob): Promise<void>;
  remove(id: string): Promise<void>;
  /** Where the rows are going RIGHT NOW: an IndexedDB store that has fallen back
   *  reports `'memory'` from then on. Optimistic before the first call — nothing
   *  has tried to open the database yet. */
  kind(): TemplateStoreKind;
}

/** In-memory store: the fallback when IndexedDB is unavailable (private windows,
 *  blocked storage) and what the unit tests exercise. User templates then last
 *  for the session only, which is still better than refusing to author one. */
export function memoryTemplateStore(): TemplateStore {
  const rows = new Map<string, { template: MockupTemplate; photo: Blob }>();
  return {
    async list() { return [...rows.values()].map((r) => ({ kind: 'user' as const, template: r.template, photoBlob: r.photo })); },
    async put(template, photo) { rows.set(template.id, { template, photo }); },
    async remove(id) { rows.delete(id); },
    kind: () => 'memory',
  };
}

const DB_VERSION = 1;
const STORE = 'templates';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

/**
 * User templates in IndexedDB: object store `templates`, keyed by the template's
 * own id, each row `{ id, template, photo }` with the photo as a Blob (structured
 * clone stores it without a base64 round-trip, which matters — these are 1–3 MB
 * JPEGs and localStorage would refuse them outright).
 *
 * Every call opens the database and closes it again rather than holding a
 * connection: the store is touched a handful of times per session (startup, a
 * save, a delete), and a held connection blocks another tab's upgrade.
 *
 * A database that refuses to OPEN (a private window that blocks storage, an
 * upgrade another tab is holding, a browser with the API present but disabled)
 * switches the whole store over to `memoryTemplateStore` for the rest of the
 * session and reports `kind() === 'memory'`, which is what the editor's and the
 * panel's one-line notice reads. Authoring a template then still works; it just
 * does not survive a reload, and the shop is told so rather than losing its work
 * silently at the next one.
 */
export function indexedDbTemplateStore(factory: IDBFactory, name = 'eip-templates'): TemplateStore {
  /** Set once the database has refused to open — one way, for the session. */
  let memory: TemplateStore | null = null;
  function open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = factory.open(name, DB_VERSION);
      req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' }); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB could not be opened'));
      req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    });
  }
  /** The open database, or null once the store has fallen back to memory. */
  async function database(): Promise<IDBDatabase | null> {
    if (memory) return null;
    try {
      return await open();
    } catch (err) {
      // Only an OPEN failure falls back. A transaction that fails on a database
      // that DID open is a real error (a quota refusal at save time) and has to
      // reach the caller — swallowing it into memory would tell the shop its
      // template was stored when it was not.
      memory = memoryTemplateStore();
      console.warn('mockup templates: browser storage is unavailable; templates are kept for this session only', err);
      return null;
    }
  }
  async function withStore<T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => Promise<T>, fallback: (store: TemplateStore) => Promise<T>): Promise<T> {
    const db = await database();
    if (!db) return fallback(memory!);
    try {
      const tx = db.transaction(STORE, mode);
      // A read is complete once its own request has resolved, so it needs no
      // completion promise — and building one it never awaits leaves an
      // unhandled rejection behind whenever the transaction fails. A WRITE does:
      // `put`/`delete` resolve long before the data is durable.
      if (mode === 'readonly') return await body(tx.objectStore(STORE));
      const done = new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      });
      const out = await body(tx.objectStore(STORE));
      await done;
      return out;
    } finally { db.close(); }
  }
  return {
    async list() {
      return withStore('readonly', async (store) => {
        const rows = await request<Array<{ id: string; template: unknown; photo: Blob }>>(store.getAll());
        const out: TemplateSource[] = [];
        for (const row of rows) {
          // A row written by an older build (or hand-edited) must not take the
          // whole registry down with it: validate each, skip what fails.
          try { out.push({ kind: 'user', template: parseTemplate(row.template), photoBlob: row.photo }); }
          catch (err) { console.warn(`mockup templates: stored template "${row.id}" is unusable and was skipped`, err); }
        }
        return out;
      }, (m) => m.list());
    },
    // `serializeTemplate`, never the live object: it is the one writer of a template's JSON (no
    // `printArea` alias beside `printAreas` for the two to drift apart, and no undefined field for
    // the structured clone to choke on), and `list` reads it straight back through `parseTemplate`.
    async put(template, photo) { await withStore('readwrite', async (store) => { store.put({ id: template.id, template: serializeTemplate(template), photo }); }, (m) => m.put(template, photo)); },
    async remove(id) { await withStore('readwrite', async (store) => { store.delete(id); }, (m) => m.remove(id)); },
    kind: () => (memory ? 'memory' : 'indexeddb'),
  };
}

/**
 * Fetch the shipped templates: `${base}/index.json` lists the styles, each
 * `${base}/${style}/index.json` carries that style's name and its templates.
 * A style that 404s, is not JSON, or holds one unparseable template is skipped
 * with a warning — the rest of the picker still works, which matters on a
 * deployment where one asset failed to upload.
 */
export async function loadBuiltinTemplates(fetchFn: typeof fetch, base = '/templates'): Promise<TemplateSource[]> {
  let styles: string[] = [];
  try {
    const res = await fetchFn(`${base}/index.json`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const index = await res.json() as { styles?: unknown };
    styles = Array.isArray(index?.styles) ? index.styles.filter((s): s is string => typeof s === 'string') : [];
  } catch (err) {
    console.warn(`mockup templates: ${base}/index.json could not be read; no built-in blanks`, err);
    return [];
  }
  const out: TemplateSource[] = [];
  for (const style of styles) {
    try {
      const res = await fetchFn(`${base}/${style}/index.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json() as { styleName?: unknown; templates?: unknown };
      const styleName = typeof body.styleName === 'string' ? body.styleName : undefined;
      const list = Array.isArray(body.templates) ? body.templates : [];
      // Parsed into a local array first: one bad template skips its whole style
      // rather than leaving half a style in the picker.
      const parsed = list.map((t) => parseTemplate(t));
      for (const template of parsed) out.push({ kind: 'builtin', template, styleName, photoUrl: `${base}/${style}/${template.photo.src}` });
    } catch (err) {
      console.warn(`mockup templates: style "${style}" was skipped`, err);
    }
  }
  return out;
}

/** `gildan-64000` → `Gildan 64000`, for a style with no index to name it (a user template). */
function styleNameFromSlug(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : slug;
}

/**
 * Group the registry by style, in first-appearance order. The session merges
 * built-ins before user templates, so built-in styles come first and a user
 * template of a built-in style joins that style's group.
 */
export function groupByStyle(sources: TemplateSource[]): Array<{ style: string; styleName: string; templates: TemplateSource[] }> {
  const groups = new Map<string, { style: string; styleName: string; templates: TemplateSource[] }>();
  for (const source of sources) {
    const style = source.template.style;
    let group = groups.get(style);
    if (!group) { group = { style, styleName: source.styleName ?? styleNameFromSlug(style), templates: [] }; groups.set(style, group); }
    // A later source may be the one carrying the shop-facing name (a user
    // template added to a built-in style has none).
    else if (source.styleName && group.styleName === styleNameFromSlug(style)) group.styleName = source.styleName;
    group.templates.push(source);
  }
  return [...groups.values()];
}

/**
 * The blanks of one style and view. Deliberately NOT filtered by `recolorable`:
 * that is `pickSource`'s job (it skips non-recolourable candidates when asked
 * for a colour they cannot wear), while a non-recolourable blank is still
 * perfectly usable in its own colour.
 */
export function candidatesFor(sources: TemplateSource[], style: string, view: MockupView): MockupTemplate[] {
  return sources.filter((s) => s.template.style === style && s.template.view === view).map((s) => s.template);
}

/** Two colours are "the same blank" under this — well inside the step between any
 *  two stock garment colours, and loose enough that a hex typed off a swatch card
 *  still lands on the photographed blank rather than recolouring it by a hair. */
const SNAP_DELTA_E = 0.05;

/**
 * Resolve a requested garment colour against the blanks of a style: a colour
 * within `SNAP_DELTA_E` (OKLab) of a blank's own colour snaps to that blank, so
 * the render uses the photographed garment instead of recolouring it to a value
 * it already is. Anything else is used as typed.
 *
 * `hex` must already be a colour `parseColor` accepts (the panel runs its input
 * through `normalizeHex` first); a bad string throws, as it does everywhere else.
 */
export function snapGarment(hex: string, blanks: MockupTemplate[]): { rgb: Rgb; snappedTo: MockupTemplate | null } {
  const rgb = parseColor(hex);
  const lab = srgbToOklab(rgb[0], rgb[1], rgb[2]);
  let best: MockupTemplate | null = null, bestD = Infinity;
  for (const b of blanks) {
    const d = deltaE(lab, srgbToOklab(b.garmentColor[0], b.garmentColor[1], b.garmentColor[2]));
    // Strictly nearer, so two blanks the same distance away resolve to the first
    // in registry order rather than the last — stable for the same input.
    if (d <= SNAP_DELTA_E && d < bestD) { bestD = d; best = b; }
  }
  return best ? { rgb: best.garmentColor, snappedTo: best } : { rgb, snappedTo: null };
}

/**
 * The template a freshly-seeded document should use: the one the shop last
 * chose if it is still in the registry (a user template can be deleted, and a
 * built-in can disappear on a redeploy), else the first blank of the first
 * style — built-ins first, since that is the order the session merges them in.
 */
export function defaultTemplateId(sources: TemplateSource[], preferred?: string): string | null {
  if (preferred && sources.some((s) => s.template.id === preferred)) return preferred;
  const groups = groupByStyle(sources);
  return groups[0]?.templates[0]?.template.id ?? null;
}
